import { useCallback, useMemo, useState } from 'react';
import { getExampleNumber } from 'libphonenumber-js';
import examples from 'libphonenumber-js/examples.mobile';
import { PhoneInput as PhoneInputLib, getCountry } from 'react-international-phone';
import 'react-international-phone/style.css';

const PREFERRED_COUNTRIES = ['uy', 'ar', 'cl', 'py', 'co', 'br', 'mx', 'pe', 'us'];
const DEFAULT_COUNTRY = 'ar';

interface PhoneInputProps {
  value: string;
  onChange: (value: string) => void;
  /** ISO2 country code (e.g. "pe", "AR"). Takes priority over dialCode. */
  defaultCountry?: string | null;
  /** Dial code with or without "+" (e.g. "+54", "51"). Used as fallback when defaultCountry is empty. */
  dialCode?: string | null;
  placeholder?: string;
  className?: string;
  inputClassName?: string;
}

function resolveCountry(
  defaultCountry: string | null | undefined,
  dialCode: string | null | undefined,
): string {
  if (defaultCountry) return defaultCountry.toLowerCase();

  if (dialCode) {
    const code = String(dialCode).replace('+', '');
    const match = getCountry({ field: 'dialCode', value: code });
    if (match) return match.iso2;
  }

  return DEFAULT_COUNTRY;
}

function getPlaceholderForCountry(iso2: string): string {
  const ex = getExampleNumber(iso2.toUpperCase() as Parameters<typeof getExampleNumber>[0], examples);
  if (!ex) return '';
  return ex.formatInternational();
}

export function PhoneInput({
  value,
  onChange,
  defaultCountry,
  dialCode,
  className = '',
  inputClassName = '',
}: PhoneInputProps) {
  const initialCountry = useMemo(
    () => resolveCountry(defaultCountry, dialCode),
    [defaultCountry, dialCode],
  );

  const [currentCountry, setCurrentCountry] = useState(initialCountry);

  const placeholder = useMemo(
    () => getPlaceholderForCountry(currentCountry),
    [currentCountry],
  );

  const handleChange = useCallback(
    (phone: string, meta: { country: { iso2: string } }) => {
      setCurrentCountry(meta.country.iso2);
      onChange(phone);
    },
    [onChange],
  );

  return (
    <PhoneInputLib
      defaultCountry={initialCountry}
      value={value}
      onChange={handleChange}
      preferredCountries={PREFERRED_COUNTRIES}
      charAfterDialCode=" "
      disableDialCodePrefill
      placeholder={placeholder}
      allowMaskOverflow
      className={`phone-input ${className}`}
      inputClassName={inputClassName}
    />
  );
}

export default PhoneInput;
